import {
  Box,
  Button,
  FormControl,
  Heading,
  HStack,
  Stack,
  Switch,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import type { IOption } from "@src/components/ui-kit";
import { ButtonLink, Select, TextField } from "@src/components/ui-kit";
import { PasswordField } from "@src/components/ui-kit/password-input";
import { API_BASE_URL } from "@src/config/constants";
import { SmtpEncryptionEnum } from "@src/stores/forms/SettingsModalState/SmtpSettingsState";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";

function getEncryptionOptions(): IOption[] {
  return [
    { value: SmtpEncryptionEnum.None, label: t`None` },
    { value: SmtpEncryptionEnum.Ssl, label: t`SSL` },
    { value: SmtpEncryptionEnum.Tls, label: t`TLS` },
    { value: SmtpEncryptionEnum.SslTls, label: t`SSL/TLS` },
    { value: SmtpEncryptionEnum.Starttls, label: t`StartTLS` },
  ];
}

export const Smtp = observer(function Smtp() {
  const {
    settingsModalStore: { smtpStore },
  } = useStore();

  const form = smtpStore.form.form.$;

  return (
    <Stack py="4" spacing="4">
      <Heading as="h3" py="2" textAlign="left" size="md">
        <Trans>SMTP Settings</Trans>
      </Heading>

      <Box>
        <Trans>
          Configure SMTP server settings for sending emails from this workspace.
        </Trans>
      </Box>

      <HStack alignItems="center" spacing="2">
        <Switch
          isChecked={form.active.value}
          onChange={({ target }) => {
            form.active.onChange(target.checked);
          }}
        />
        <Trans>Active</Trans>
      </HStack>

      {smtpStore.isOauthConnected ? (
        <>
          {smtpStore.isGoogleConnected ? (
            <Box>
              <ButtonLink
                isExternal
                href={`${API_BASE_URL}/google-smtp/disconnect`}
                target="_self"
              >
                <Trans>Disconnect Google</Trans>
              </ButtonLink>
            </Box>
          ) : null}

          {smtpStore.isMicrosoftConnected ? (
            <Box>
              <ButtonLink
                isExternal
                href={`${API_BASE_URL}/microsoft-smtp/disconnect`}
                target="_self"
              >
                <Trans>Disconnect Microsoft</Trans>
              </ButtonLink>
            </Box>
          ) : null}
        </>
      ) : (
        <>
          <HStack spacing="2">
            <ButtonLink
              isExternal
              href={`${API_BASE_URL}/google-smtp/auth`}
              target="_self"
            >
              <Trans>Connect Google</Trans>
            </ButtonLink>
            <ButtonLink
              isExternal
              href={`${API_BASE_URL}/microsoft-smtp/auth`}
              target="_self"
            >
              <Trans>Connect Microsoft</Trans>
            </ButtonLink>
          </HStack>

          <form autoComplete="off">
            <VStack align="start" w="2/3" spacing="4">
              <TextField
                label={t`SMTP Host`}
                placeholder={t`smtp.example.com`}
                value={form.smtp_host.value}
                onChange={form.smtp_host.onChange}
                error={form.smtp_host.error}
              />

              <TextField
                label={t`SMTP Port`}
                placeholder="587"
                type="number"
                value={form.smtp_port.value}
                onChange={form.smtp_port.onChange}
                error={form.smtp_port.error}
              />

              <TextField
                label={t`SMTP Username`}
                placeholder={t`username@example.com`}
                value={form.smtp_username.value}
                onChange={form.smtp_username.onChange}
                error={form.smtp_username.error}
                autoComplete="off"
              />

              <PasswordField
                label={t`SMTP Password`}
                placeholder={t`Enter password`}
                value={form.smtp_password.value}
                onChange={form.smtp_password.onChange}
                error={form.smtp_password.error}
                autoComplete="off"
              />

              <FormControl isInvalid={!!form.smtp_encryption.error}>
                <Select
                  label={t`SMTP Encryption`}
                  onChange={form.smtp_encryption.onChange}
                  value={form.smtp_encryption.value}
                  options={getEncryptionOptions()}
                  error={form.smtp_encryption.error}
                />
              </FormControl>

              <TextField
                label={t`From Email Address`}
                placeholder={t`noreply@example.com`}
                type="email"
                value={form.smtp_from_address.value}
                onChange={form.smtp_from_address.onChange}
                error={form.smtp_from_address.error}
              />

              <TextField
                label={t`From Name`}
                placeholder={t`My Company`}
                value={form.smtp_from_name.value}
                onChange={form.smtp_from_name.onChange}
                error={form.smtp_from_name.error}
              />

              <HStack>
                <Button
                  isDisabled={smtpStore.isTestButtonDisabled}
                  isLoading={smtpStore.isLoadingSmtpTest}
                  onClick={smtpStore.testSmtpConnection}
                  variant="outline"
                >
                  <Trans>Test SMTP Connection</Trans>
                </Button>
                <Button
                  isDisabled={smtpStore.isTestButtonDisabled}
                  isLoading={smtpStore.isLoadingSendTestEmail}
                  onClick={smtpStore.sendTestEmail}
                  variant="outline"
                >
                  <Trans>Send test email</Trans>
                </Button>
              </HStack>
            </VStack>
          </form>
        </>
      )}
    </Stack>
  );
});
